import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import type { VisitorRow , VisitorStatus } from '@/types/dashboard'
import { getSourceIcon, getBrowserIcon, getCountryFlag, getOSEmoji } from '@/lib/icons'
import { features } from '@/config/features'

interface TabbedVisitorSectionProps {
  visitors: VisitorRow[]
  isLoading?: boolean
  onVisitorClick?: (visitorHash: string, dogName: string) => void
}

type TabId = 'user' | 'journey'

// Aggregated visitor with multiple sessions
interface AggregatedVisitor {
  visitorHash: string
  dogName: string
  countryCode: string | null
  city: string | null
  lastSeenAt: string
  status: VisitorStatus
  isConverted: boolean
  browser: string | null
  os: string | null
  deviceType: 'desktop' | 'mobile' | 'tablet' | null
  pageviewCount: number
  referrer: string | null
  // Aggregated data
  sessionCount: number
  sessionIds: string[]
  totalRevenue: number
  timeToConvert: number | null
  // Most recent session for linking
  latestSessionId: string
}

function formatTimeToConvert(seconds: number): string {
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`
  const hours = Math.floor(seconds / 3600)
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''}`
  const days = Math.floor(hours / 24)
  return `${days} day${days > 1 ? 's' : ''}`
}

function getSourceFromReferrer(referrer: string | null): { icon: React.ReactNode; label: string } {
  if (!referrer) return { icon: '🔗', label: 'Direct/None' }

  try {
    const url = new URL(referrer)
    const hostname = url.hostname.replace('www.', '')

    // Map to known sources
    if (hostname.includes('google')) return { icon: getSourceIcon('google'), label: 'Google' }
    if (hostname.includes('facebook') || hostname.includes('fb.com')) return { icon: getSourceIcon('facebook'), label: 'Facebook' }
    if (hostname.includes('twitter') || hostname.includes('t.co') || hostname.includes('x.com')) return { icon: getSourceIcon('x'), label: 'X' }
    if (hostname.includes('instagram')) return { icon: getSourceIcon('instagram'), label: 'Instagram' }
    if (hostname.includes('youtube')) return { icon: getSourceIcon('youtube'), label: 'YouTube' }
    if (hostname.includes('linkedin')) return { icon: getSourceIcon('linkedin'), label: 'LinkedIn' }
    if (hostname.includes('tiktok')) return { icon: getSourceIcon('tiktok'), label: 'TikTok' }
    if (hostname.includes('reddit')) return { icon: getSourceIcon('reddit'), label: 'Reddit' }

    return { icon: '🌐', label: hostname }
  } catch {
    return { icon: '🔗', label: referrer.slice(0, 20) }
  }
}

function ActivityDots({ pageCount }: { pageCount: number }) {
  const maxDots = 6
  const filledDots = Math.min(pageCount, maxDots)
  const emptyDots = maxDots - filledDots

  return (
    <div className="flex gap-0.5">
      {Array.from({ length: filledDots }).map((_, i) => (
        <span key={`filled-${i}`} className="w-1.5 h-1.5 rounded-full bg-primary-400" />
      ))}
      {Array.from({ length: emptyDots }).map((_, i) => (
        <span key={`empty-${i}`} className="w-1.5 h-1.5 rounded-full bg-gray-200" />
      ))}
    </div>
  )
}

function VisitorTableRow({
  visitor,
  showRevenue,
  showConversionBadge,
  onRowClick,
}: {
  visitor: AggregatedVisitor
  showRevenue?: boolean
  showConversionBadge?: boolean
  onRowClick?: () => void
}) {
  const source = getSourceFromReferrer(visitor.referrer)
  const lastSeen = formatDistanceToNow(new Date(visitor.lastSeenAt), { addSuffix: false })

  return (
    <tr className="group border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
      {/* Visitor info */}
      <td className="px-4 py-3">
        <Link to={`/sessions/${visitor.latestSessionId}`} className="block">
          <div className="flex items-center gap-2">
            <span className="text-lg flex-shrink-0">
              {visitor.deviceType === 'mobile' ? '📱' : visitor.deviceType === 'tablet' ? '📱' : '🖥️'}
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900 truncate">
                  {visitor.dogName}
                </span>
                {showConversionBadge && visitor.isConverted && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700 flex-shrink-0">
                    Customer
                  </span>
                )}
                {visitor.status === 'live' && (
                  <span className="flex items-center gap-1 text-xs text-green-600 flex-shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                    Live
                  </span>
                )}
              </div>
              {/* Tags row */}
              <div className="flex flex-wrap items-center gap-2 mt-0.5 text-xs text-gray-500">
                {visitor.countryCode && (
                  <span className="flex items-center gap-1">
                    <span>{getCountryFlag(visitor.countryCode)}</span>
                    <span>{visitor.city || visitor.countryCode}</span>
                  </span>
                )}
                {visitor.os && (
                  <span className="flex items-center gap-1">
                    <span>{getOSEmoji(visitor.os)}</span>
                    <span>{visitor.os}</span>
                  </span>
                )}
                {visitor.browser && (
                  <span className="flex items-center gap-1">
                    <span className="w-3.5 h-3.5">{getBrowserIcon(visitor.browser)}</span>
                    <span>{visitor.browser}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </Link>
      </td>

      {/* Source */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">{source.icon}</span>
          <span className="truncate">{source.label}</span>
        </div>
      </td>

      {/* Sessions count */}
      <td className="px-4 py-3 text-center">
        <Link
          to={`/sessions?visitor=${visitor.visitorHash}`}
          className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700 hover:bg-primary-100 hover:text-primary-700 transition-colors"
          title={`View all ${visitor.sessionCount} sessions`}
        >
          {visitor.sessionCount}
        </Link>
      </td>

      {/* Spent - only show when revenue features enabled */}
      {features.conversionBadges && (
        <td className="px-4 py-3 text-right">
          {showRevenue && visitor.totalRevenue > 0 ? (
            <span className="font-medium text-green-600">
              ${visitor.totalRevenue.toFixed(0)}
            </span>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </td>
      )}

      {/* Time to convert (for journey tab) - only show when revenue features enabled */}
      {features.conversionBadges && showRevenue && (
        <td className="px-4 py-3 text-right text-gray-600 text-sm">
          {visitor.timeToConvert ? formatTimeToConvert(visitor.timeToConvert) : '-'}
        </td>
      )}

      {/* Last seen */}
      <td className="px-4 py-3 text-right text-gray-500 text-sm">
        {lastSeen}
      </td>

      {/* Activity */}
      <td className="px-4 py-3">
        <ActivityDots pageCount={visitor.pageviewCount} />
      </td>

      {/* Filter action */}
      {onRowClick && (
        <td className="px-2 py-3">
          <button
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onRowClick()
            }}
            className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 opacity-0 group-hover:opacity-100 transition-all"
            title="Filter dashboard by this visitor"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
          </button>
        </td>
      )}
    </tr>
  )
}

export function TabbedVisitorSection({ visitors, isLoading, onVisitorClick }: TabbedVisitorSectionProps) {
  const [activeTab, setActiveTab] = useState<TabId>('user')
  const [searchQuery, setSearchQuery] = useState('')

  // Deduplicate visitors by visitorHash, aggregating session data
  const aggregatedVisitors = useMemo(() => {
    const visitorMap = new Map<string, AggregatedVisitor>()

    for (const v of visitors) {
      const existing = visitorMap.get(v.visitorHash)

      if (existing) {
        // Update with more recent data if this session is newer
        const isNewer = new Date(v.lastSeenAt) > new Date(existing.lastSeenAt)

        existing.sessionCount++
        existing.sessionIds.push(v.sessionId)
        existing.pageviewCount += v.pageviewCount
        existing.totalRevenue += v.revenueAmount || 0

        if (v.isConverted) {
          existing.isConverted = true
          // Keep earliest time to convert
          if (v.timeToConvert && (!existing.timeToConvert || v.timeToConvert < existing.timeToConvert)) {
            existing.timeToConvert = v.timeToConvert
          }
        }

        if (isNewer) {
          existing.lastSeenAt = v.lastSeenAt
          existing.status = v.status
          existing.latestSessionId = v.sessionId
          // Update device info if newer session has it
          if (v.browser) existing.browser = v.browser
          if (v.os) existing.os = v.os
          if (v.deviceType) existing.deviceType = v.deviceType
          if (v.countryCode) existing.countryCode = v.countryCode
          if (v.city) existing.city = v.city
        }

        // Keep the first referrer (original source)
        if (!existing.referrer && v.referrer) {
          existing.referrer = v.referrer
        }
      } else {
        // Create new aggregated entry
        visitorMap.set(v.visitorHash, {
          visitorHash: v.visitorHash,
          dogName: v.dogName,
          countryCode: v.countryCode,
          city: v.city,
          lastSeenAt: v.lastSeenAt,
          status: v.status,
          isConverted: v.isConverted,
          browser: v.browser,
          os: v.os,
          deviceType: v.deviceType,
          pageviewCount: v.pageviewCount,
          referrer: v.referrer || null,
          sessionCount: 1,
          sessionIds: [v.sessionId],
          totalRevenue: v.revenueAmount || 0,
          timeToConvert: v.timeToConvert || null,
          latestSessionId: v.sessionId,
        })
      }
    }

    // Convert to array and sort by lastSeenAt (most recent first)
    return Array.from(visitorMap.values()).sort(
      (a, b) => new Date(b.lastSeenAt).getTime() - new Date(a.lastSeenAt).getTime()
    )
  }, [visitors])

  // Filter visitors based on tab and search
  const filteredVisitors = useMemo(() => {
    return aggregatedVisitors.filter(v => {
      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        const matchesName = v.dogName.toLowerCase().includes(query)
        const matchesCountry = v.countryCode?.toLowerCase().includes(query)
        const matchesCity = v.city?.toLowerCase().includes(query)
        if (!matchesName && !matchesCountry && !matchesCity) return false
      }

      // Apply tab filter
      if (activeTab === 'journey') {
        return v.isConverted
      }
      return true
    })
  }, [aggregatedVisitors, searchQuery, activeTab])

  const convertedCount = aggregatedVisitors.filter(v => v.isConverted).length
  const uniqueVisitorCount = aggregatedVisitors.length

  return (
    <div className="section-card">
      {/* Header with tabs and search */}
      <div className="flex items-center justify-between border-b border-gray-100">
        <div className="flex">
          <button
            onClick={() => setActiveTab('user')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'user'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            👥 Users
            <span className="ml-1.5 text-xs text-gray-400">({uniqueVisitorCount})</span>
          </button>
          {features.conversionBadges && (
            <button
              onClick={() => setActiveTab('journey')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'journey'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              💰 Conversions
              <span className="ml-1.5 text-xs text-gray-400">({convertedCount})</span>
            </button>
          )}
        </div>

        {/* Search */}
        <div className="px-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search visitors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-48 pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <svg
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="p-8 text-center text-gray-500">
          <div className="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-2" />
          Loading visitors...
        </div>
      )}

      {/* Empty state */}
      {!isLoading && filteredVisitors.length === 0 && (
        <div className="p-8 text-center">
          {activeTab === 'journey' ? (
            <>
              <span className="text-3xl">💰</span>
              <p className="mt-2 text-gray-600">No conversions yet</p>
              <p className="text-sm text-gray-500">Converted visitors will appear here</p>
            </>
          ) : searchQuery ? (
            <>
              <span className="text-3xl">🔍</span>
              <p className="mt-2 text-gray-600">No visitors found</p>
              <p className="text-sm text-gray-500">Try a different search term</p>
            </>
          ) : (
            <>
              <span className="text-3xl">👥</span>
              <p className="mt-2 text-gray-600">No visitors yet</p>
              <p className="text-sm text-gray-500">Visitors will appear here once they browse your site</p>
            </>
          )}
        </div>
      )}

      {/* Visitor table */}
      {!isLoading && filteredVisitors.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-xs font-medium text-gray-500 border-b border-gray-100">
                <th className="px-4 py-2 text-left min-w-[200px]">Visitor</th>
                <th className="px-4 py-2 text-left w-[120px]">Source</th>
                <th className="px-4 py-2 text-center w-[80px]">Sessions</th>
                {features.conversionBadges && <th className="px-4 py-2 text-right w-[80px]">Spent</th>}
                {features.conversionBadges && activeTab === 'journey' && <th className="px-4 py-2 text-right w-[90px]">Time</th>}
                <th className="px-4 py-2 text-right w-[90px]">Last seen</th>
                <th className="px-4 py-2 text-left w-[80px]">Activity</th>
                {onVisitorClick && <th className="px-2 py-2 w-[40px]"></th>}
              </tr>
            </thead>
            <tbody className="max-h-[400px] overflow-y-auto">
              {filteredVisitors.map((visitor) => (
                <VisitorTableRow
                  key={visitor.visitorHash}
                  visitor={visitor}
                  showRevenue={activeTab === 'journey'}
                  showConversionBadge={features.conversionBadges}
                  onRowClick={
                    onVisitorClick
                      ? () => onVisitorClick(visitor.visitorHash, visitor.dogName)
                      : undefined
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer */}
      {filteredVisitors.length > 0 && (
        <div className="border-t border-gray-100 p-3 text-center">
          <Link
            to="/sessions"
            className="text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            View all sessions →
          </Link>
        </div>
      )}
    </div>
  )
}
